import { ReactNode, useEffect, useRef, useState, useCallback } from "react";
import {
  useSession,
  useActiveOrganization,
  useListOrganizations,
  organization,
} from "@/lib/auth-client";
import { AuthContext, AuthContextValue, AuthStatus } from "./AuthContext";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { data: session, isPending: sessionLoading } = useSession();
  const { data: activeOrg, isPending: orgLoading } = useActiveOrganization();
  const { data: orgs, isPending: orgsLoading } = useListOrganizations();
  const [memberRole, setMemberRole] = useState<string | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);

  // Refs to prevent duplicate API calls
  const hasAutoSelected = useRef(false);
  const lastFetchedOrgId = useRef<string | null>(null);

  // Stable IDs for dependency arrays (prevents infinite loops)
  const sessionUserId = session?.user?.id;
  const activeOrgId = activeOrg?.id;
  const firstOrgId = orgs?.[0]?.id;

  // Auto-select organization if user has memberships but no active org
  useEffect(() => {
    if (
      !sessionLoading &&
      !orgLoading &&
      !orgsLoading &&
      sessionUserId &&
      !activeOrgId &&
      firstOrgId &&
      !hasAutoSelected.current
    ) {
      hasAutoSelected.current = true;
      console.log("[Auth] Auto-selecting organization:", firstOrgId);
      organization.setActive({ organizationId: firstOrgId }).catch((err) => {
        console.error("[Auth] Failed to auto-select organization:", err);
        hasAutoSelected.current = false;
      });
    }
  }, [
    sessionUserId,
    activeOrgId,
    firstOrgId,
    sessionLoading,
    orgLoading,
    orgsLoading,
  ]);

  // Fetch the user's role in the active organization
  // Only fetch when org ID changes to prevent infinite loops
  useEffect(() => {
    // Skip if no org or no user - memberRole will be derived as null in context value
    if (!activeOrgId || !sessionUserId) {
      // Reset the fetch tracker so we re-fetch when org becomes available
      lastFetchedOrgId.current = null;
      return;
    }

    // Skip if we already fetched for this org
    if (lastFetchedOrgId.current === activeOrgId) {
      return;
    }

    lastFetchedOrgId.current = activeOrgId;
    let cancelled = false;

    // Use async IIFE to handle loading state in async context
    (async () => {
      setRoleLoading(true);
      try {
        const { data, error } = await organization.getActiveMemberRole();
        if (cancelled) return;

        if (error) {
          console.error("[Auth] Failed to get member role:", error);
          setMemberRole(null);
        } else {
          console.log("[Auth] Member role:", data?.role);
          setMemberRole(data?.role ?? null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[Auth] Error fetching member role:", err);
        setMemberRole(null);
      } finally {
        if (!cancelled) {
          setRoleLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeOrgId, sessionUserId]);

  // Map Better Auth organization role to our app role
  const mapRole = useCallback(
    (betterAuthRole?: string | null): "user" | "admin" | "super_admin" => {
      switch (betterAuthRole) {
        case "owner":
          return "super_admin";
        case "admin":
          return "admin";
        case "member":
        default:
          return "user";
      }
    },
    [],
  );

  // Derive effective role - null if no active org or user
  const effectiveRole = activeOrgId && sessionUserId ? memberRole : null;
  const userRole = mapRole(effectiveRole);

  // Determine auth status
  // Approval is based on organization membership, not emailVerified
  // (Magic link users are inherently verified, and invited users are pre-approved)
  let status: AuthStatus = "loading";

  if (sessionLoading || orgLoading || orgsLoading) {
    status = "loading";
  } else if (!session) {
    status = "unauthorized";
  } else if (!activeOrg && (!orgs || orgs.length === 0)) {
    // User has no organization memberships - needs admin to invite them
    status = "no_organization";
  } else if (!activeOrg && orgs && orgs.length > 0) {
    status = "loading"; // Auto-selecting in progress
  } else if (roleLoading) {
    status = "loading"; // Fetching role
  } else {
    status = "authorized";
  }

  const value: AuthContextValue = {
    status,
    user: session
      ? {
          id: session.user.id,
          tenantId: activeOrg?.id || "",
          email: session.user.email,
          firstName: session.user.name?.split(" ")[0],
          lastName: session.user.name?.split(" ").slice(1).join(" "),
          role: userRole,
          isActive: session.user.emailVerified,
          // Terms acceptance is fetched via useCurrentUser() from /api/me
          acceptedTermsAt: null,
          termsVersion: null,
          tenant: activeOrg
            ? {
                id: activeOrg.id,
                name: activeOrg.name,
                slug: activeOrg.slug,
                templateId: "wallace",
                primaryColor: undefined,
                logoUrl: activeOrg.logo || undefined,
              }
            : null,
        }
      : null,
    tenant: activeOrg
      ? {
          id: activeOrg.id,
          name: activeOrg.name,
          slug: activeOrg.slug,
          templateId: "wallace",
          primaryColor: undefined,
          logoUrl: activeOrg.logo || undefined,
        }
      : null,
    isAdmin: userRole !== "user",
    isSuperAdmin: userRole === "super_admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
